import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import styled from 'styled-components'
import { ChevronLeft, Link as LinkIcon } from 'lucide-react'
import {
  PiIdentificationCard,
  PiHandshake,
  PiNotepad,
  PiCalendarStar,
  PiHandCoins,
  PiEnvelope,
  PiBuildings,
} from 'react-icons/pi'
import { FaInstagram } from 'react-icons/fa'
import useApiService from 'services/api'
import { MobileColors } from 'styles/mobileTokens'
import { formatInteger, formatDate2 } from 'utils/formatting'
import { MobileAccountAttributesSummary } from 'components/MobileCompanyPage/MobileAccountAttributes'
import { MobileDealsAccordion } from 'components/MobileCompanyPage/MobileDealsAccordion'
import { MobileEventHistoryAccordion } from 'components/MobileCompanyPage/MobileEventHistory'
import { MobileRemindersAccordion } from 'components/MobileCompanyPage/MobileReminders'
import { useCompanyOrDoorData } from 'hooks/useCompanyOrDoorData'
import { FirstBiteDataDrawer } from 'components/MobileCompanyPage/FirstBiteDataDrawer'
import { IContactCompany } from 'models/contact_companies'
import { BackButton } from 'components/Mobile/Mobile.styles'
import { listSampleRequests } from 'services/samples'

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #07311e;
  overflow-y: auto;
  padding-bottom: 200px; /* Space for drawer handle */
`

const CompanyHeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: ${MobileColors.backgroundWhite};
  border-bottom: 1px solid ${MobileColors.borderDefault};
`

const CompanyHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`

const CompanyLogoContainer = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid ${MobileColors.borderDefault};
`

const CompanyLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

// Logo-less companies (universities, hospitals, …) show a Buildings icon in a
// gray circle — matches the desktop CompanyHeader fallback (bg-gray-200 /
// text-gray-700 PiBuildings).
const EmptyLogo = styled.div`
  width: 100%;
  height: 100%;
  background-color: #e5e7eb; /* gray-200 */
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
    color: #374151; /* gray-700 */
  }
`

const CompanyName = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: ${MobileColors.textPrimary};
  margin: 0;
  flex: 1;
`

const CompanyMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  color: ${MobileColors.textSecondary};
`

const MetaDivider = styled.span`
  color: ${MobileColors.borderDefault};
`

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${MobileColors.textSecondary};
  transition: color 0.2s;

  &:hover {
    color: ${MobileColors.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const ActionButtonsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr); /* 6 buttons in one line */
  gap: 4px; /* smaller gap */
  padding: 12px 10px;
  background: ${MobileColors.primary};
  border-bottom: 1px solid #07311e;
`

const ActionButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px;
  background: ${MobileColors.primaryActive};
  border: none;
  border-radius: 8px;
  margin: 3px;
  color: #ffffff;
  font-size: 9px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  svg {
    font-size: 20px;
    color: #ffffff;
  }

  &:hover {
    background: ${MobileColors.primary};
  }

  &:active {
    background: ${MobileColors.primaryDark};
  }
`

const ActionCount = styled.span`
  font-size: 11px;
  font-weight: 300;
`

const ContentSection = styled.div`
  padding: 12px;
  background: #07311e;
`

const SectionDivider = styled.div`
  height: 8px;
  background: ${MobileColors.backgroundOverlay};
`

const NotFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${MobileColors.backgroundOverlay};
`

/** Shared back-button + message empty state for any mobile company page. */
export function MobileCompanyNotFound({ label }: { label: string }) {
  const navigate = useNavigate()
  return (
    <NotFoundContainer>
      <BackButton onClick={() => navigate(-1)}>
        <ChevronLeft size={20} />
        <span>Back</span>
      </BackButton>
      <div className="flex h-full items-center justify-center">
        <p className="text-gray-500">{label}</p>
      </div>
    </NotFoundContainer>
  )
}

export interface MobileCompanyPageShellProps {
  /** The contact-company id — drives action buttons, accordions, and counts. */
  contactCompanyId: number
  /**
   * Restaurant-chain id used by the drawer's "Create Deal / Add Contact"
   * quick-actions (they route to `/deals/create?chain_id=` → `useGetChain`).
   * Only meaningful for chains — leave undefined for non-restaurant types so
   * the drawer falls back to its "coming soon" state instead of fetching the
   * wrong company by id collision.
   */
  entityId?: number
  companyName: string
  domain?: string | null
  /** Restaurant chains have logos; institutional types render the empty logo. */
  logo?: string | null
  /** Header "N locations" — omit when not applicable to the type. */
  locationCount?: number | null
  instagramUrl?: string | null
  /** Tab content for the First Bite data drawer (built per type). */
  summaryContent: React.ReactNode[]
  locationDetailsContent: React.ReactNode[]
}

/**
 * Shared chrome for every mobile company page (chains + the non-restaurant
 * types). Owns the back button, header, the six quick-action buttons, the four
 * data accordions, and the First Bite data drawer — all keyed off
 * `contactCompanyId`, so it is fully company-type agnostic. The type-specific
 * stats live in `summaryContent` / `locationDetailsContent`, which each page
 * builds via the shared `companyTypeData` helpers.
 */
export function MobileCompanyPageShell({
  contactCompanyId,
  entityId,
  companyName,
  domain,
  logo,
  locationCount,
  instagramUrl,
  summaryContent,
  locationDetailsContent,
}: MobileCompanyPageShellProps) {
  const navigate = useNavigate()
  const api = useApiService()

  const { data: companyData } = useCompanyOrDoorData(contactCompanyId)

  // Reminders count — `contact_company` (not `contact_company_id`) matches the
  // backend filter; exclude inactive statuses to match MobileRemindersAccordion.
  const { data: remindersData } = useQuery({
    queryKey: ['reminders-count', contactCompanyId],
    queryFn: () =>
      api.getReminders({
        contact_company: contactCompanyId,
        page: 1,
        limit: 1,
        status__exclude: ['sent', 'ended', 'deleted', 'paused'],
      }),
    enabled: !!contactCompanyId,
  })

  const { data: samplesData } = useQuery({
    queryKey: ['samples-count', contactCompanyId],
    queryFn: () =>
      listSampleRequests({
        contact_company_id: contactCompanyId,
        page: 1,
        page_size: 1,
      }),
    enabled: !!contactCompanyId,
  })

  const remindersCount = remindersData?.count || 0
  const samplesCount = samplesData?.count || 0

  const hasNoFirstBiteData = useMemo(() => {
    if (!companyData) return false
    const contactCount = companyData?.contact_count || 0
    const dealCount = companyData?.deal_count || 0
    const noteCount = companyData?.note_count || 0
    const emailCount = companyData?.email_count || 0

    return (
      contactCount === 0 &&
      dealCount === 0 &&
      noteCount === 0 &&
      emailCount === 0 &&
      remindersCount === 0 &&
      samplesCount === 0
    )
  }, [companyData, remindersCount, samplesCount])

  const modifiedDate =
    'modified' in (companyData || {})
      ? (companyData as IContactCompany)?.modified
      : undefined

  const encodedName = encodeURIComponent(companyName || '')

  return (
    <>
      <PageContainer>
        <BackButton onClick={() => navigate(-1)}>
          <ChevronLeft size={20} />
          <span>Back</span>
        </BackButton>

        {/* Company Header */}
        <CompanyHeaderSection>
          <CompanyHeaderRow>
            <CompanyLogoContainer>
              {logo ? (
                <CompanyLogo src={logo} alt={companyName || ''} />
              ) : (
                <EmptyLogo>
                  <PiBuildings />
                </EmptyLogo>
              )}
            </CompanyLogoContainer>
            <CompanyName>{companyName}</CompanyName>
          </CompanyHeaderRow>

          <CompanyMetaRow>
            {!!locationCount && (
              <span>{formatInteger(locationCount)} locations</span>
            )}
            {modifiedDate && (
              <>
                <MetaDivider>•</MetaDivider>
                <span>Updated {formatDate2(modifiedDate)}</span>
              </>
            )}
            {domain && (
              <>
                <MetaDivider>•</MetaDivider>
                <SocialLink
                  href={
                    domain.startsWith('http') ? domain : `https://${domain}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Visit website"
                >
                  <LinkIcon />
                </SocialLink>
              </>
            )}
            {instagramUrl && (
              <SocialLink
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="View Instagram"
              >
                <FaInstagram />
              </SocialLink>
            )}
          </CompanyMetaRow>
        </CompanyHeaderSection>

        {/* Quick actions */}
        <ActionButtonsRow>
          <ActionButton
            onClick={() =>
              navigate(`/contacts?contact_company_id=${contactCompanyId}`)
            }
          >
            <PiIdentificationCard />
            <ActionCount>({companyData?.contact_count || 0})</ActionCount>
          </ActionButton>
          <ActionButton
            onClick={() =>
              navigate(`/deals?contact_company_id=${contactCompanyId}`)
            }
          >
            <PiHandshake />
            <ActionCount>({companyData?.deal_count || 0})</ActionCount>
          </ActionButton>
          <ActionButton
            onClick={() =>
              navigate(
                `/notes?contact_company_id=${contactCompanyId}&company_name=${encodedName}`
              )
            }
          >
            <PiNotepad />
            <ActionCount>({companyData?.note_count || 0})</ActionCount>
          </ActionButton>
          <ActionButton
            onClick={() =>
              navigate(`/reminders?contact_company_id=${contactCompanyId}`)
            }
          >
            <PiCalendarStar />
            <ActionCount>({remindersCount})</ActionCount>
          </ActionButton>
          <ActionButton
            onClick={() =>
              navigate(
                `/samples?contact_company_id=${contactCompanyId}&company_name=${encodedName}`
              )
            }
          >
            <PiHandCoins />
            <ActionCount>({samplesCount})</ActionCount>
          </ActionButton>
          <ActionButton
            onClick={() =>
              navigate(
                `/emails?contact_company_id=${contactCompanyId}&company_name=${encodedName}`
              )
            }
          >
            <PiEnvelope />
            <ActionCount>({companyData?.email_count || 0})</ActionCount>
          </ActionButton>
        </ActionButtonsRow>

        {/* Account Attributes */}
        <ContentSection>
          <MobileAccountAttributesSummary
            chainProxyId={contactCompanyId}
            chainName={companyName}
          />
        </ContentSection>

        <SectionDivider />

        {/* Deals */}
        <ContentSection>
          <MobileDealsAccordion chainProxyId={contactCompanyId} />
        </ContentSection>

        <SectionDivider />

        {/* Reminders */}
        <ContentSection>
          <MobileRemindersAccordion chainProxyId={contactCompanyId} />
        </ContentSection>

        <SectionDivider />

        {/* Event History */}
        <ContentSection>
          <MobileEventHistoryAccordion chainProxyId={contactCompanyId} />
        </ContentSection>
      </PageContainer>

      {/* First Bite Data Drawer — fixed-position, outside the scroll container */}
      <FirstBiteDataDrawer
        chainID={entityId}
        chainProxyId={contactCompanyId}
        chainName={companyName}
        logo={logo ?? undefined}
        summaryContent={summaryContent}
        locationDetailsContent={locationDetailsContent}
        reducedHeight={hasNoFirstBiteData}
      />
    </>
  )
}
